import Rights from '../../../../common/services/Rights';
import Session, { IS_SELLSIDE } from '../../../../common/services/Session';
import { isEligibilityExceptionsEnabled } from './featureFlags';

interface ExceptionProgram {
  exceptionAvailable?: boolean;
  exceptionsApplied?: boolean;
}

// The "Manage Eligibility Exceptions" user right (rightId 30, mapped in Rights.tsx).
export const hasManageExceptionRight = (): boolean =>
  Rights.manageEligibilityExceptions === true;

// Eligibility exceptions are buy-side only; never surface the apply/manage
// actions on a sell-side transaction.
const isSellSide = (): boolean => Session.get(IS_SELLSIDE) === true;

// Whether the program's investor allows exceptions (per-program qualification flag).
export const isExceptionAvailable = (program: ExceptionProgram): boolean =>
  program?.exceptionAvailable === true;

// Apply Exception button shows when the feature is enabled, not sell side, the user has the right, and the investor allows exceptions.
export const canShowApplyException = (program: ExceptionProgram): boolean =>
  isEligibilityExceptionsEnabled() &&
  !isSellSide() &&
  hasManageExceptionRight() &&
  isExceptionAvailable(program);

// Whether a program carries an applied exception (per-program `exceptionsApplied` flag).
// Buy-side only: the flag persists on the sell-side rates response, so gate it out.
export const isExceptionProgram = (program: ExceptionProgram): boolean =>
  isEligibilityExceptionsEnabled() &&
  !isSellSide() &&
  program?.exceptionsApplied === true;

// Whether the loan has any applied exception; gates the "Exception Applied" filter.
export const hasActiveException = (
  programs: ExceptionProgram[] = [],
): boolean =>
  isEligibilityExceptionsEnabled() &&
  !isSellSide() &&
  (programs ?? []).some((program) => program?.exceptionsApplied === true);

// Manage Exception button shows when the feature is enabled, not sell side, the user has the right, and the program has an applied exception.
export const canManageException = (program: ExceptionProgram): boolean =>
  isEligibilityExceptionsEnabled() &&
  !isSellSide() &&
  hasManageExceptionRight() &&
  isExceptionProgram(program);
